// ts-rest contract for the digital fact find (PRD #70).
//
// The fact find is the canonical client circumstances document. FCA COBS 9
// (suitability) and the Consumer Duty both presume it exists. It is the
// single authoritative artefact with a version history, an audit trail, an
// ATR score, a CFL calculation and a refresh trigger.
//
// # The fact-find handle (SEP-261)
//
// A fact find has its OWN identity. `factFindId` is the handle, and the
// SUI-#### `reference` is the human-readable name of it. A fact find that
// exists from before SEP-261 keeps the client id as its handle, so an old link
// still opens. A fact find of a JOINT set carries a fresh handle, names the
// Contact it is about in `subjectContactId`, and shares `clientId`,
// `suitabilityRecordId` and `jointGroupId` with its siblings.
//
// Endpoints under /api/fact-find/*:
//   GET  /api/fact-find/clients                      — register: one row per fact find
//   GET  /api/fact-find/client-options               — clients for the create typeahead
//   GET  /api/fact-find/client-contacts              — the contacts a joint set can name
//   GET  /api/fact-find/contact-fact-finds           — the fact finds of one contact
//   POST /api/fact-find/create                       — manual create (existing / new / joint)
//   GET  /api/fact-find/:factFindId                    — current fact find + versions
//   POST /api/fact-find/:factFindId/draft              — create a new draft version
//   PATCH /api/fact-find/:factFindId/section           — save a section's answers
//   POST /api/fact-find/:factFindId/atr                — save the ATR response set
//   POST /api/fact-find/:factFindId/cfl                — save the CFL inputs
//   POST /api/fact-find/:factFindId/transition         — move the draft through its lifecycle
//   POST /api/fact-find/:factFindId/annotation         — add an IM annotation
//   POST /api/fact-find/:factFindId/refresh            — author a section-targeted refresh
//   GET  /api/fact-find/:factFindId/prefill            — list ATLAS pre-fill suggestions
//   POST /api/fact-find/prefill/:suggestionId/accept — accept a pre-fill suggestion
//   POST /api/fact-find/prefill/:suggestionId/reject — reject a pre-fill suggestion
//   GET  /api/fact-find/:factFindId/document           — generated-document meta (Slice 7)
//   POST /api/fact-find/:factFindId/document           — generate/regenerate the filled PDF
//   GET  /api/fact-find/:factFindId/document/filled     — download the filled PDF (binary,
//                                                       a plain Nest route, not ts-rest)
//   GET  /api/fact-find/:factFindId/signing             — signing state (Slice 9)
//   POST /api/fact-find/:factFindId/send-for-signature  — DocuSign envelope send (Slice 9)
//   POST /api/fact-find/:factFindId/mark-signed         — manual mark-signed (Slice 9)
//   POST /api/fact-find/:factFindId/draft-client-email  — draft a client cover note (Outlook / Gmail)
//   POST /api/fact-find/:factFindId/document/upload-signed — manual signed-PDF upload
//                                                       (multipart, a plain Nest route)
//   GET  /api/fact-find/:factFindId/document/signed     — download the signed PDF (binary,
//                                                       a plain Nest route, not ts-rest)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Lifecycle status of a fact-find version — mirrors the state machine. */
export const factFindStatusSchema = z.enum([
    'draft',
    'submitted',
    'reviewed',
    'approved',
    'withdrawn',
    // Terminal soft-delete state (DESK 9). Set on the working version when the
    // whole fact find is deleted; the fact find is retained in Postgres but
    // hidden from the register / detail. Distinct from `withdrawn`.
    'deleted',
    // An approved SUI that a newer approved SUI replaced, or that stayed more
    // than 10 calendar days past its expiry date (SEP-288). The system sets it.
    // No transition request can.
    'expired',
]);
export type FactFindStatus = z.infer<typeof factFindStatusSchema>;

/** Coarse freshness band for the register. */
export const factFindFreshnessSchema = z.enum(['fresh', 'refresh_due', 'in_progress', 'overdue']);
export type FactFindFreshness = z.infer<typeof factFindFreshnessSchema>;

/**
 * Discriminator selecting the canonical Dowgate form (PRD #70 Slice 1):
 * `individual` (`suitability-fact-find.pdf`, the default) or `entity`
 * (`entity-fact-find.pdf`). Nullable on the wire — legacy rows pre-date the
 * column and the consumer treats null as `individual`.
 */
export const factFindClientTypeSchema = z.enum(['individual', 'entity']);
export type FactFindClientType = z.infer<typeof factFindClientTypeSchema>;

export const cflBandSchema = z.enum(['low', 'medium', 'high']);
export type CflBand = z.infer<typeof cflBandSchema>;

export const prefillReviewStatusSchema = z.enum(['pending', 'accepted', 'rejected']);
export type PrefillReviewStatus = z.infer<typeof prefillReviewStatusSchema>;

export const vulnerabilityDriverSchema = z.enum([
    'health',
    'life_event',
    'resilience',
    'capability',
]);
export type VulnerabilityDriver = z.infer<typeof vulnerabilityDriverSchema>;

// ---------------------------------------------------------------------------
// Response value + section state
// ---------------------------------------------------------------------------

/** A question answer — string, number, boolean or list. `null` = unanswered. */
export const factFindAnswerSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
]);
export type FactFindAnswer = z.infer<typeof factFindAnswerSchema>;

/** The origin of one stored response. */
export const factFindResponseSourceSchema = z.enum([
    'client',
    'im',
    'atlas_prefill',
    'client_application',
    'previous_fact_find',
    // An answer that the import of a Dynamics KYC V5 fact find wrote.
    'crm_import',
]);
export type FactFindResponseSource = z.infer<typeof factFindResponseSourceSchema>;

/** The two sources that seed a stored answer. */
export const factFindSeededSourceSchema = z.enum(['client_application', 'previous_fact_find']);
export type FactFindSeededSource = z.infer<typeof factFindSeededSourceSchema>;

/** A single per-question response within a version. */
export const factFindResponseSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    value: factFindAnswerSchema,
    /**
     * Where the value came from: client entry, IM entry, ATLAS pre-fill, the
     * application the client submitted (SEP-263.29), or an earlier fact find of
     * the same person (SEP-261). The last two are stored answers that the
     * server seeded. They keep their source until the IM changes the value, and
     * the form shows them grey until then.
     */
    source: factFindResponseSourceSchema,
    updatedAt: z.string(),
});
export type FactFindResponse = z.infer<typeof factFindResponseSchema>;

/**
 * Risk-assessment responses + the computed score for a version (the Dowgate
 * "Tolerance & Capacity for Risk" instrument). Responses key off question id
 * and store the SELECTED OPTION LABEL (a string); the scorer maps label →
 * score.
 */
export const atrAssessmentSchema = z.object({
    /** question id -> selected option label. */
    responses: z.record(z.string(), z.string()),
    /** Sum of the selected option scores (0..120). */
    total: z.number(),
    /** Continuous 1.0-7.0 score. */
    rawScore: z.number(),
    /** Integer 1-7 band. */
    band: z.number().int().min(1).max(7),
    /** House mandate the band maps onto. */
    mandate: z.string(),
    /** Whether all questions are answered. */
    complete: z.boolean(),
    /** Low-capacity signals for the Vulnerable Clients surface. */
    capacityFlags: z.array(z.string()),
    /** True when any capacity flag is set. */
    lowCapacity: z.boolean(),
    computedAt: z.string(),
    /**
     * The questions whose answer the server seeded from an earlier statement,
     * with the source of each one (SEP-261, SEP-263). The instrument keeps its
     * answers here and not in the response list, so the source lives beside
     * them. A question leaves the map when the IM changes its answer.
     */
    prefilled: z.record(z.string(), factFindSeededSourceSchema).optional(),
});
export type AtrAssessment = z.infer<typeof atrAssessmentSchema>;

/** CFL inputs + the computed working for a version. */
export const cflWorkingStepSchema = z.object({
    label: z.string(),
    amount: z.number(),
    note: z.string(),
});
export type CflWorkingStep = z.infer<typeof cflWorkingStepSchema>;

export const cflCalculationSchema = z.object({
    annualIncome: z.number(),
    essentialExpenditure: z.number(),
    liquidAssets: z.number(),
    knownCommitments: z.number(),
    portfolioValue: z.number(),
    safetyBuffer: z.number(),
    netSurplus: z.number(),
    freeLiquidAssets: z.number(),
    absorbableLoss: z.number(),
    maxDrawdownPct: z.number(),
    band: cflBandSchema,
    working: z.array(cflWorkingStepSchema),
    computedAt: z.string(),
});
export type CflCalculation = z.infer<typeof cflCalculationSchema>;

/** A vulnerability marker flagged from the vulnerability-indicators section. */
export const factFindVulnerabilityMarkerSchema = z.object({
    driver: vulnerabilityDriverSchema,
    questionId: z.string(),
    detail: z.string(),
});
export type FactFindVulnerabilityMarker = z.infer<typeof factFindVulnerabilityMarkerSchema>;

/** An IM annotation against a section. */
export const factFindAnnotationSchema = z.object({
    id: z.string(),
    sectionId: z.string(),
    author: z.string(),
    note: z.string(),
    kind: z.enum(['note', 'gap', 'inconsistency']),
    createdAt: z.string(),
});
export type FactFindAnnotation = z.infer<typeof factFindAnnotationSchema>;

/** An immutable audit entry — who/what/when/why. */
export const factFindAuditEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    by: z.string(),
    action: z.string(),
    detail: z.string().optional(),
    /** The field that the event changed (SEP-288 section 9). */
    field: z.string().optional(),
    /** The value before the change. */
    previousValue: z.string().optional(),
    /** The value after the change. */
    newValue: z.string().optional(),
    /** The reason that the user gave, where the change needs one. */
    reason: z.string().optional(),
});
export type FactFindAuditEntry = z.infer<typeof factFindAuditEntrySchema>;

// ---------------------------------------------------------------------------
// FactFindVersion — an immutable historical snapshot
// ---------------------------------------------------------------------------

/**
 * The two doors a completed client fact find arrives through (SEP-243
 * decision D2). `staff_upload` is the IM or EA upload through the client
 * Documents door. `portal_link` is the client portal magic link, and that door
 * is DEFERRED: nothing writes the value yet.
 */
export const factFindReceiptDoorSchema = z.enum(['staff_upload', 'portal_link']);
export type FactFindReceiptDoor = z.infer<typeof factFindReceiptDoorSchema>;

/**
 * The form layout of one assessment (SEP-288). `v5` is a historic Dynamics KYC
 * V5 assessment that the import recreated. `v6` is the Septima form.
 */
export const factFindFormVersionSchema = z.enum(['v5', 'v6']);
export type FactFindFormVersion = z.infer<typeof factFindFormVersionSchema>;

/**
 * The paper form that the questions of a Septima assessment follow (SEP-288).
 *
 * - `existing_client` is the "Suitability Fact Find - Existing Clients" v6
 *   form. Each new fact find of a person uses it: the onboarding of a new
 *   client and the suitability refresh of an existing client. The
 *   `formAudience` of the version selects the questions and the words of
 *   each audience (Dowgate decision of 2026-10-21).
 * - `entity_suitability` is the "Suitability Entity Fact Find - Existing
 *   Entity Clients" v2 form (SEP-351). Each new fact find of an entity uses it,
 *   also with a `formAudience`.
 * - `new_client` is the "Suitability Fact Find" that onboarding used before
 *   that decision. No new fact find uses it. A version that holds it keeps it,
 *   and it shows and prints as before.
 */
export const factFindFormLayoutSchema = z.enum([
    'new_client',
    'existing_client',
    'entity_suitability',
]);
export type FactFindFormLayout = z.infer<typeof factFindFormLayoutSchema>;

/**
 * The person that the questions of a form are for. `onboarding` is a new
 * client, and `refresh` is the suitability refresh of an existing client. The
 * `existing_client` and the `entity_suitability` forms tag some sections and
 * questions with one audience. A tagged item shows only to that audience.
 */
export const factFindFormAudienceSchema = z.enum(['onboarding', 'refresh']);
export type FactFindFormAudience = z.infer<typeof factFindFormAudienceSchema>;

/**
 * The audience of one version. Null reads as `refresh`, because each
 * `existing_client` and `entity_suitability` version made before the audience
 * was recorded is a refresh, or it reads in the words of a refresh. The
 * `new_client` form has no tagged item, so the value has no effect on it.
 */
export function factFindAudienceOf(version: {
    readonly formAudience?: FactFindFormAudience | null;
}): FactFindFormAudience {
    return version.formAudience ?? 'refresh';
}

/** True when an item with this tag shows to the audience. No tag shows to each audience. */
export function isForFactFindAudience(
    tag: FactFindFormAudience | undefined,
    audience: FactFindFormAudience,
): boolean {
    return tag === undefined || tag === audience;
}

/** One section of a historic V5 assessment, word for word from the export. */
export const factFindSourceSectionSchema = z.object({
    title: z.string(),
    fields: z.array(z.object({ label: z.string(), value: z.string() })),
});
export type FactFindSourceSection = z.infer<typeof factFindSourceSectionSchema>;

export const factFindSourceRecordSchema = z.object({
    sections: z.array(factFindSourceSectionSchema),
});
export type FactFindSourceRecord = z.infer<typeof factFindSourceRecordSchema>;

export const factFindVersionSchema = z.object({
    id: z.string(),
    /**
     * The SUI-#### number of this assessment (SEP-288). Each version is one
     * SUI. Null on an old version that the migration could not name.
     */
    reference: z.string().nullable(),
    /** The form layout. Null reads as `v6`. */
    formVersion: factFindFormVersionSchema.nullable(),
    /** The paper form that the questions follow. Null reads as `new_client`. */
    formLayout: factFindFormLayoutSchema.nullable(),
    /** The person that the questions are for. Null reads as `refresh`. */
    formAudience: factFindFormAudienceSchema.nullable(),
    /** The Fact Find Expiry Date of this SUI. */
    expiresAt: z.string().nullable(),
    /** When the status became `expired`. */
    expiredAt: z.string().nullable(),
    /** The Risk Profile Result as the assessment recorded it. */
    riskProfile: z.string().nullable(),
    /** The CRM status text of an imported V5 assessment. */
    sourceStatus: z.string().nullable(),
    /** The historic V5 answers, word for word. Null on a V6 version. */
    sourceRecord: factFindSourceRecordSchema.nullable(),
    /** The client of the fact find. A joint set shares one client. */
    clientId: z.string(),
    /**
     * The fact find this version belongs to (SEP-261). `clientId` alone cannot
     * name it any more: a joint set holds several fact finds under one client.
     */
    factFindId: z.string(),
    /**
     * The Suitability Record this version was made under (SEP-353). The SUR is
     * the target of a fact find. Null on a version of a fact find that had no
     * SUR, for example the fact find of an onboarding prospect.
     */
    suitabilityRecordId: z.string().nullable(),
    /** Which canonical Dowgate form this version is for. Null ⇒ `individual`. */
    clientType: factFindClientTypeSchema.nullable(),
    /** Monotonic version number, starting at 1. */
    versionNumber: z.number().int(),
    status: factFindStatusSchema,
    /** Per-question responses. */
    responses: z.array(factFindResponseSchema),
    atr: atrAssessmentSchema.nullable(),
    cfl: cflCalculationSchema.nullable(),
    vulnerabilityMarkers: z.array(factFindVulnerabilityMarkerSchema),
    annotations: z.array(factFindAnnotationSchema),
    auditLog: z.array(factFindAuditEntrySchema),
    /** Reason this version exists — initial / refresh / annual review. */
    reason: z.string(),
    /** Section ids a refresh version targets (empty for a full fact find). */
    refreshSections: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.string(),
    /** Set once the version reaches `approved` — then the row is immutable. */
    approvedAt: z.string().nullable(),
    approvedBy: z.string().nullable(),
    /**
     * The receipt lock (SEP-243 sections 6 and 40). A non-null
     * `receiptLockedAt` means the client answers of this version are frozen:
     * every answer edit is refused with the `fact_find_receipt_locked` code.
     * The lifecycle and every read continue to work.
     *
     * The SUBMISSION freezes the answers as well (SEP-257 item 17). A version
     * whose `status` is not `draft` refuses every answer edit with the
     * `fact_find_submitted_locked` code, whatever this column holds.
     */
    receiptLockedAt: z.string().nullable(),
    /** The staff user who confirmed the receipt. Null for the portal door. */
    receiptLockedByUserId: z.string().nullable(),
    /** Which door the client answers arrived through. */
    receiptDoor: factFindReceiptDoorSchema.nullable(),
    /**
     * The stored document that evidences the receipt. It is the SourceUpload
     * that the client Documents door holds (ADR-0018). The staff door always
     * carries one, and the portal door carries none.
     */
    receiptSourceUploadId: z.string().nullable(),
    /**
     * True for a version that pre-dates the rule. Such a version holds no
     * original of record. A real receipt clears the mark.
     */
    legacyUnlocked: z.boolean().nullable(),
});
export type FactFindVersion = z.infer<typeof factFindVersionSchema>;

// ---------------------------------------------------------------------------
// FactFind — one current record per fact-find handle (SEP-261)
// ---------------------------------------------------------------------------

/** One fact find of a joint set, as the tab strip of its siblings reads it. */
export const factFindSiblingSchema = z.object({
    factFindId: z.string(),
    reference: z.string().nullable(),
    /** The name of the person this fact find is about. */
    subjectName: z.string().nullable(),
    /** The 1-based place in the set. */
    jointPosition: z.number().int(),
    /** The status of the newest version, for the tab marker. */
    status: factFindStatusSchema,
});
export type FactFindSibling = z.infer<typeof factFindSiblingSchema>;

export const factFindSchema = z.object({
    /**
     * The handle of this fact find (SEP-261). It equals `clientId` on a fact
     * find that pre-dates the joint build-out, and it is a fresh id on a fact
     * find of a joint set. Every `/api/fact-find/:factFindId` route takes it.
     */
    factFindId: z.string(),
    /** The SUI-#### reference Septima minted for this fact find. */
    reference: z.string().nullable(),
    clientId: z.string(),
    clientName: z.string(),
    /** The Contact this fact find is about. Null when it names the client alone. */
    subjectContactId: z.string().nullable(),
    /** The name of that Contact, for the title and the register. */
    subjectName: z.string().nullable(),
    /** The SUR this fact find falls under. */
    suitabilityRecordId: z.string().nullable(),
    /** One value shared by the fact finds of one joint set. Null when sole. */
    jointGroupId: z.string().nullable(),
    /** The 1-based place in the joint set. Null when sole. */
    jointPosition: z.number().int().nullable(),
    /** How many fact finds the joint set holds. Null when sole. */
    jointSize: z.number().int().nullable(),
    /** Every fact find of the joint set, in position order. Empty when sole. */
    siblings: z.array(factFindSiblingSchema),
    /** Which canonical Dowgate form this fact find uses. Null ⇒ `individual`. */
    clientType: factFindClientTypeSchema.nullable(),
    /** The user id of the IM that the fact find stores. */
    im: z.string(),
    /**
     * The name of the IM, from the book of the client now, as the register
     * gives it (SEP-356). "Unknown" when the id names no user. Empty when the
     * fact find has no IM.
     */
    imName: z.string(),
    /** Id of the working (draft / submitted / reviewed) version, if any. */
    currentVersionId: z.string().nullable(),
    /** Id of the latest approved version, if any. */
    approvedVersionId: z.string().nullable(),
    /** Every version, newest first. */
    versions: z.array(factFindVersionSchema),
    /** Freshness band derived from the latest approved version age. */
    freshness: factFindFreshnessSchema,
    /** Age in days of the latest approved version (−1 if never approved). */
    ageDays: z.number().int(),
    /** Portal magic-link token for the working version. */
    portalToken: z.string(),
    /**
     * The CRM Contact this fact find registered on approval (SEP-209). Null on
     * an entity fact find, and on one that has never been approved.
     */
    contactId: z.string().nullable(),
});
export type FactFind = z.infer<typeof factFindSchema>;

/** Register row — one per fact find (SEP-261 decision 4). */
export const factFindRegisterRowSchema = z.object({
    /** The handle the register row opens. */
    factFindId: z.string(),
    /** The SUI-#### reference of the fact find. */
    reference: z.string().nullable(),
    clientId: z.string(),
    clientName: z.string(),
    /** The person the fact find is about. Null when it names the client alone. */
    subjectName: z.string().nullable(),
    /** The 1-based place in the joint set. Null when sole. */
    jointPosition: z.number().int().nullable(),
    /** How many fact finds the joint set holds. Null when sole. */
    jointSize: z.number().int().nullable(),
    /**
     * The NAME of the IM (SEP-356), never a user id: the current manager of
     * the book of the client, two names joined with ", " when two books hold
     * the client. "Unknown" when the id names no user. Empty when the fact
     * find has no IM.
     */
    im: z.string(),
    freshness: factFindFreshnessSchema,
    status: factFindStatusSchema,
    completenessPct: z.number().int(),
    ageDays: z.number().int(),
    atrBand: z.number().int().nullable(),
    cflBand: cflBandSchema.nullable(),
    vulnerabilityFlag: z.boolean(),
    versionCount: z.number().int(),
    lastUpdated: z.string(),
});
export type FactFindRegisterRow = z.infer<typeof factFindRegisterRowSchema>;

// ---------------------------------------------------------------------------
// Risk summary — the approved fact find's risk score for the Vulnerable
// Clients surface (Slice 8). It comes from the `desk`-readable FactFind,
// because the VulnerabilityRecord and the VulnerabilityFlag give no risk score.
// ---------------------------------------------------------------------------

export const factFindRiskSummaryRowSchema = z.object({
    /** The fact find this score comes from. A client can hold several. */
    factFindId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    /** The person the fact find is about. Null when it names the client alone. */
    subjectName: z.string().nullable(),
    /** Source version's lifecycle status — approved when an approved version exists. */
    status: factFindStatusSchema,
    /** Integer 1-7 risk band, or null when the version carries no scored ATR. */
    band: z.number().int().min(1).max(7).nullable(),
    /** House mandate the band maps onto, or null when unscored. */
    mandate: z.string().nullable(),
    /** Sum of selected option scores (0..120), or null when unscored. */
    total: z.number().nullable(),
    /** Low-capacity signals (income security / emergency funds / loss tolerance / horizon). */
    capacityFlags: z.array(z.string()),
    /** True when any capacity flag is set. */
    lowCapacity: z.boolean(),
});
export type FactFindRiskSummaryRow = z.infer<typeof factFindRiskSummaryRowSchema>;

export const factFindRiskSummaryResponseSchema = z.object({
    rows: z.array(factFindRiskSummaryRowSchema),
});
export type FactFindRiskSummaryResponse = z.infer<typeof factFindRiskSummaryResponseSchema>;

// ---------------------------------------------------------------------------
// FactFindDocument — the generated, signable fact-find PDF (Slice 7)
// ---------------------------------------------------------------------------

/** Signing lifecycle of the generated document. */
export const factFindSigningStatusSchema = z.enum([
    'none',
    'sent',
    'partially_signed',
    'completed',
    'declined',
    'voided',
]);
export type FactFindSigningStatus = z.infer<typeof factFindSigningStatusSchema>;

/** A signing recipient on the generated document. */
export const factFindDocumentRecipientSchema = z.object({
    name: z.string(),
    email: z.string(),
    status: z.string(),
});
export type FactFindDocumentRecipient = z.infer<typeof factFindDocumentRecipientSchema>;

/**
 * Generated fact-find document META — the projection of a `FactFindDocument`
 * row WITHOUT the raw PDF bytes (those download via the binary route). The
 * `hasFilledPdf` / `hasSignedPdf` booleans tell the UI which downloads /
 * signing affordances to offer.
 */
export const factFindDocumentSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    factFindVersionId: z.string(),
    clientType: factFindClientTypeSchema,
    signingMethod: z.enum(['docusign', 'manual']).nullable(),
    docusignEnvelopeId: z.string().nullable(),
    signingStatus: factFindSigningStatusSchema,
    recipients: z.array(factFindDocumentRecipientSchema).nullable(),
    hasFilledPdf: z.boolean(),
    hasSignedPdf: z.boolean(),
    createdAt: z.string(),
    sentAt: z.string().nullable(),
    completedAt: z.string().nullable(),
});
export type FactFindDocument = z.infer<typeof factFindDocumentSchema>;

/** Generate / regenerate response — the document meta, or null if absent. */
export const factFindDocumentResponseSchema = z.object({
    ok: z.boolean(),
    document: factFindDocumentSchema.nullable(),
});
export type FactFindDocumentResponse = z.infer<typeof factFindDocumentResponseSchema>;

// ---------------------------------------------------------------------------
// Signing (Slice 9) — DocuSign send + manual fallback
// ---------------------------------------------------------------------------

/**
 * Signing state the client fact-find view reads to decide which signing
 * affordances to offer. `docusignConfigured` gates the "Send via DocuSign"
 * action — the manual path (download / upload-signed / mark-signed) is always
 * available. The booleans + status mirror the stored document row.
 */
export const factFindSigningStateSchema = z.object({
    /** Whether DocuSign is configured on the server (gates the send affordance). */
    docusignConfigured: z.boolean(),
    signingStatus: factFindSigningStatusSchema,
    signingMethod: z.enum(['docusign', 'manual']).nullable(),
    hasFilledPdf: z.boolean(),
    hasSignedPdf: z.boolean(),
    recipients: z.array(factFindDocumentRecipientSchema).nullable(),
});
export type FactFindSigningState = z.infer<typeof factFindSigningStateSchema>;

export const factFindSigningStateResponseSchema = z.object({
    ok: z.boolean(),
    signing: factFindSigningStateSchema,
});
export type FactFindSigningStateResponse = z.infer<typeof factFindSigningStateResponseSchema>;

/** A signer the IM supplies when sending for DocuSign signature. */
export const sendForSignatureSignerSchema = z.object({
    name: z.string().min(1),
    email: z.string().email(),
});
export type SendForSignatureSigner = z.infer<typeof sendForSignatureSignerSchema>;

export const sendForSignatureRequestSchema = z.object({
    by: z.string(),
    signers: z.array(sendForSignatureSignerSchema).min(1),
    /** Optional override for the DocuSign email subject. */
    emailSubject: z.string().optional(),
});
export type SendForSignatureRequest = z.infer<typeof sendForSignatureRequestSchema>;

export const markSignedRequestSchema = z.object({
    by: z.string(),
});
export type MarkSignedRequest = z.infer<typeof markSignedRequestSchema>;

/** Which connected mailbox the cover-note draft lands in. */
export const draftEmailProviderSchema = z.enum(['gmail', 'outlook']);
export type DraftEmailProvider = z.infer<typeof draftEmailProviderSchema>;

/**
 * Request to draft a client cover-note email into the IM's connected mailbox.
 * `by` is the IM display name used in the sign-off + audit; the mailbox token
 * is keyed by the authenticated user server-side, never by this field.
 * `provider` selects Outlook (default) or Gmail — the token for the chosen
 * provider must be connected for the signed-in user.
 */
export const draftClientEmailRequestSchema = z.object({
    by: z.string(),
    provider: draftEmailProviderSchema.default('outlook'),
});
export type DraftClientEmailRequest = z.infer<typeof draftClientEmailRequestSchema>;

export const draftClientEmailResponseSchema = z.object({
    ok: z.boolean(),
    /** Provider draft id (Gmail draft id / Graph message id). */
    draftId: z.string(),
    /** Deep link to the draft in the chosen mailbox for the IM to review + send. */
    webUrl: z.string(),
});
export type DraftClientEmailResponse = z.infer<typeof draftClientEmailResponseSchema>;

// ---------------------------------------------------------------------------
// ATLAS pre-fill suggestion
// ---------------------------------------------------------------------------

export const prefillSuggestionSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    sectionId: z.string(),
    questionId: z.string(),
    suggestedValue: factFindAnswerSchema,
    sourceCallId: z.string(),
    sourceQuote: z.string(),
    confidence: z.enum(['high', 'medium', 'low']),
    reviewStatus: prefillReviewStatusSchema,
    createdAt: z.string(),
    reviewedAt: z.string().nullable(),
    reviewedBy: z.string().nullable(),
});
export type PrefillSuggestion = z.infer<typeof prefillSuggestionSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

const factFindResponseBody = z.object({
    ok: z.boolean(),
    factFind: factFindSchema,
});

export const factFindRegisterResponseSchema = z.object({
    rows: z.array(factFindRegisterRowSchema),
});
export type FactFindRegisterResponse = z.infer<typeof factFindRegisterResponseSchema>;

export const createDraftRequestSchema = z.object({
    by: z.string(),
    reason: z.string().default('Initial fact find'),
    /** When set, the draft is a section-targeted refresh. */
    refreshSections: z.array(z.string()).default([]),
    /**
     * Which canonical Dowgate form the draft is for. Defaults to `individual`.
     * A refresh draft inherits the fact find's existing `clientType` and
     * ignores this. (Slice 3 surfaces the Individual / Entity choice in the UI.)
     */
    clientType: factFindClientTypeSchema.default('individual'),
});
export type CreateDraftRequest = z.infer<typeof createDraftRequestSchema>;

// ---------------------------------------------------------------------------
// Manual create flow (PRD #70 Slice 3) — start a fact find for an existing
// Client by id, or "from scratch" for a brand-new minimal Client.
// ---------------------------------------------------------------------------

/** A client option for the manual-create typeahead — identity only. */
export const factFindClientOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
    /**
     * How the book of record styles the Client, used to suggest the form tab.
     * Null when the source row carries no classification.
     */
    clientKind: z.string().nullable(),
    /** Whether this client already has a fact find (the register has a row). */
    hasFactFind: z.boolean(),
    /**
     * The references of the SURs that hold a fact find of this client
     * (SEP-353). A SUR of a new client covers no account yet, so its fact find
     * is the only link to the client.
     */
    recordReferences: z.array(z.string()),
});
export type FactFindClientOption = z.infer<typeof factFindClientOptionSchema>;

export const factFindClientsResponseSchema = z.object({
    clients: z.array(factFindClientOptionSchema),
});
export type FactFindClientsResponse = z.infer<typeof factFindClientsResponseSchema>;

/**
 * One account of an account group, as the fact-find record pickers show it
 * (SEP-353): the reference of the account and the wrapper types of its
 * portfolios, for example ISA, SIPP or GIA.
 */
export const factFindRecordAccountSchema = z.object({
    clientId: z.string(),
    /** The name of the account group (the Client of Septima). */
    clientName: z.string().nullable(),
    accountId: z.string(),
    accountReference: z.string().nullable(),
    wrapperTypes: z.array(z.string()),
});
export type FactFindRecordAccount = z.infer<typeof factFindRecordAccountSchema>;

/** A contact that contributes to a Suitability Record (SEP-353). */
export const factFindRecordContactSchema = z.object({
    contactId: z.string(),
    name: z.string(),
    /** The role the contact holds on the record, in plain words. */
    roleLabel: z.string().nullable(),
});
export type FactFindRecordContact = z.infer<typeof factFindRecordContactSchema>;

/** The newest version of the fact find of a Suitability Record (SEP-353). */
export const factFindRecordLatestSchema = z.object({
    factFindId: z.string(),
    versionId: z.string(),
    /** The SUI-#### number of the version. */
    reference: z.string().nullable(),
    status: factFindStatusSchema,
    /** The date of the version: the source date of a historic V5 SUI. */
    date: z.string(),
    formVersion: factFindFormVersionSchema.nullable(),
});
export type FactFindRecordLatest = z.infer<typeof factFindRecordLatestSchema>;

/**
 * One Suitability Record that a fact find can start on (SEP-353). The SUR is
 * the target of a fact find. The picker and the refresh preview show what the
 * SUR covers and who contributes to it, so an IM never needs to remember a SUR
 * number.
 */
export const factFindRecordOptionSchema = z.object({
    recordId: z.string(),
    reference: z.string(),
    /** The words that name the SUR: the entity, the contacts or the account groups. */
    label: z.string(),
    assessmentType: factFindClientTypeSchema,
    status: z.enum(['draft', 'active', 'closed']),
    ownerImName: z.string().nullable(),
    /** The accounts that the SUR covers. */
    accounts: z.array(factFindRecordAccountSchema),
    /** The contacts that contribute to the SUR. */
    contacts: z.array(factFindRecordContactSchema),
    /** THE fact find of the SUR. Null when the SUR holds none yet. */
    factFindId: z.string().nullable(),
    /** The newest version of the fact finds of the SUR, by its date. */
    latestFactFind: factFindRecordLatestSchema.nullable(),
    /** True when a version of the SUR is still open. A new fact find then waits. */
    hasOpenFactFind: z.boolean(),
    /**
     * The next review date of the SUR. When the SUR holds none, the expiry
     * date of its current approved SUI, which is when the refresh is due.
     */
    nextReviewDate: z.string().nullable(),
});
export type FactFindRecordOption = z.infer<typeof factFindRecordOptionSchema>;

export const factFindRecordOptionsResponseSchema = z.object({
    rows: z.array(factFindRecordOptionSchema),
});
export type FactFindRecordOptionsResponse = z.infer<typeof factFindRecordOptionsResponseSchema>;

/**
 * One account group that a new Suitability Record can cover (SEP-353), with
 * its accounts and the SURs that cover it already.
 */
export const factFindAccountGroupOptionSchema = z.object({
    clientId: z.string(),
    clientName: z.string().nullable(),
    clientKind: z.string().nullable(),
    accounts: z.array(
        z.object({
            accountId: z.string(),
            accountReference: z.string().nullable(),
            wrapperTypes: z.array(z.string()),
        }),
    ),
    /** The SURs that cover an account of the group now. */
    records: z.array(z.object({ recordId: z.string(), reference: z.string() })),
});
export type FactFindAccountGroupOption = z.infer<typeof factFindAccountGroupOptionSchema>;

export const factFindAccountGroupOptionsResponseSchema = z.object({
    groups: z.array(factFindAccountGroupOptionSchema),
});
export type FactFindAccountGroupOptionsResponse = z.infer<
    typeof factFindAccountGroupOptionsResponseSchema
>;

/**
 * One fact find on the page of the person it is about (SEP-261). The server
 * names the person with the rule of the previous answers, so the page and the
 * suggestions always agree. The page opens the fact find by `factFindId`.
 */
export const contactFactFindSchema = z.object({
    /** The handle the row opens. */
    factFindId: z.string(),
    /** The SUI-#### reference of the fact find. */
    reference: z.string().nullable(),
    clientId: z.string(),
    clientName: z.string(),
    /** The person a fact find of a joint set names. Null on a sole one. */
    subjectName: z.string().nullable(),
    /** The 1-based place in the joint set. Null when sole. */
    jointPosition: z.number().int().nullable(),
    /** How many fact finds the joint set holds. Null when sole. */
    jointSize: z.number().int().nullable(),
    /** The status of the newest version. Null when the fact find holds no version. */
    status: factFindStatusSchema.nullable(),
    /** The number of the newest version. Null when the fact find holds no version. */
    versionNumber: z.number().int().nullable(),
    /** When the newest version last changed. Null when the fact find holds no version. */
    lastUpdated: z.string().nullable(),
});
export type ContactFactFind = z.infer<typeof contactFactFindSchema>;

export const contactFactFindsResponseSchema = z.object({
    rows: z.array(contactFactFindSchema),
});
export type ContactFactFindsResponse = z.infer<typeof contactFactFindsResponseSchema>;

/**
 * Manual fact-find creation for a client that no Suitability Record covers
 * yet: an existing client (`clientId`), for example a prospect, or a brand-new
 * client (`newClient`) that the server materialises as a minimal Client row.
 * Exactly one of the two is required. A client that a SUR covers starts its
 * fact find on the SUR instead (SEP-353): `createNewFactFind` for an existing
 * SUR, and `createSuitabilityRecordWithFactFind` for a new SUR.
 */
export const createFactFindRequestSchema = z
    .object({
        by: z.string(),
        /** Which canonical Dowgate form to open — the active dialog tab. */
        clientType: factFindClientTypeSchema,
        /** Point at an existing Client that no SUR covers. */
        clientId: z.string().min(1).optional(),
        /** ...or supply a brand-new client to materialise "from scratch". */
        newClient: z.object({ name: z.string().min(1) }).optional(),
        /**
         * Start the fact find with no Suitability Record (SEP-354). Absent or
         * false opens a new SUR for the client, then the fact find on it.
         */
        withoutSuitabilityRecord: z.boolean().optional(),
    })
    // Exactly one of clientId / newClient — a flat object + refine keeps the
    // inferred type small (an intersection-of-union here inflates the whole
    // apiContract type past tsc's serialization limit — TS7056).
    .refine((d) => Boolean(d.clientId) !== Boolean(d.newClient), {
        message: 'Provide exactly one of clientId or newClient.',
    });
export type CreateFactFindRequest = z.infer<typeof createFactFindRequestSchema>;

/**
 * Manual-create response. `factFindId` is the handle the dialog opens — the
 * first fact find of a joint set. `createdFactFindIds` holds every handle the
 * request made, in position order.
 */
export const createFactFindResponseSchema = z.object({
    ok: z.boolean(),
    clientId: z.string(),
    factFindId: z.string(),
    createdFactFindIds: z.array(z.string()),
    factFind: factFindSchema,
});
export type CreateFactFindResponse = z.infer<typeof createFactFindResponseSchema>;

const cflInputsSchema = z.object({
    annualIncome: z.number(),
    essentialExpenditure: z.number(),
    liquidAssets: z.number(),
    knownCommitments: z.number(),
    portfolioValue: z.number(),
});

/**
 * Atomic working-version save for the IM Desk fill surface — commits every
 * section's answers plus (optionally) the ATR responses and CFL inputs in one
 * write, replacing the per-section save endpoints for the Desk editor. The
 * actor is always the IM (this is the `desk` surface), so it is implied. Omit
 * `atr` / `cfl` to leave that part of the working version unchanged.
 */
export const saveWorkingRequestSchema = z.object({
    by: z.string(),
    /** sectionId -> (questionId -> answer). Visible answers only; merged onto the working version. */
    sections: z.record(z.string(), z.record(z.string(), factFindAnswerSchema)),
    /** ATR responses (questionId -> selected option label). Omit to leave ATR unchanged. */
    atr: z.record(z.string(), z.string()).optional(),
    /** CFL inputs. Omit to leave CFL unchanged. */
    cfl: cflInputsSchema.optional(),
});
export type SaveWorkingRequest = z.infer<typeof saveWorkingRequestSchema>;

export const factFindTransitionRequestSchema = z.object({
    to: factFindStatusSchema,
    by: z.string(),
    note: z.string().optional(),
});
export type FactFindTransitionRequest = z.infer<typeof factFindTransitionRequestSchema>;

/** Soft-delete a fact find (DESK 9). `reason` is captured for the audit trail. */
export const deleteFactFindRequestSchema = z.object({
    by: z.string(),
    reason: z.string().optional(),
});
export type DeleteFactFindRequest = z.infer<typeof deleteFactFindRequestSchema>;

export const deleteFactFindResponseSchema = z.object({ ok: z.literal(true) });
export type DeleteFactFindResponse = z.infer<typeof deleteFactFindResponseSchema>;

/**
 * An administrator deletes a fact find and the rows that exist only for it
 * (SEP-355). Distinct from the soft delete above, which only hides it.
 */
export const adminDeleteFactFindRequestSchema = z.object({
    reason: z.string().max(500).optional(),
    /**
     * The administrator confirms the delete of a record of advice: an
     * approved or expired SUI, a final approval, or a signed PDF. Without it,
     * such a delete answers 409 with the code `fact_find_delete_refused`.
     */
    confirmKept: z.boolean().optional(),
});
export type AdminDeleteFactFindRequest = z.infer<typeof adminDeleteFactFindRequestSchema>;

export const adminDeleteFactFindResponseSchema = z.object({
    ok: z.literal(true),
    /** The SUI numbers that the delete removed. Septima never gives them again. */
    references: z.array(z.string()),
});
export type AdminDeleteFactFindResponse = z.infer<typeof adminDeleteFactFindResponseSchema>;

export const addAnnotationRequestSchema = z.object({
    by: z.string(),
    sectionId: z.string(),
    note: z.string().min(1),
    kind: z.enum(['note', 'gap', 'inconsistency']).default('note'),
});
export type AddAnnotationRequest = z.infer<typeof addAnnotationRequestSchema>;

export const refreshRequestSchema = z.object({
    by: z.string(),
    sections: z.array(z.string()).min(1),
    message: z.string().default(''),
});
export type RefreshRequest = z.infer<typeof refreshRequestSchema>;

export const refreshResponseSchema = z.object({
    ok: z.boolean(),
    factFind: factFindSchema,
    /** Stub portal URL the section refresh was sent to. */
    refreshUrl: z.string(),
});
export type RefreshResponse = z.infer<typeof refreshResponseSchema>;

/**
 * Confirm the receipt of the client-completed fact find, and freeze the
 * original answers of the working version (SEP-243 sections 6 and 40).
 *
 * `sourceUploadId` names the document that the client Documents door already
 * stored (`POST /api/documents/:clientId` with `area: 'fact_find'`). There is
 * one uploader, and this endpoint does not add a second one. The document is
 * mandatory, because it is the evidence of the receipt (acceptance point 6).
 *
 * This is the STAFF door, and it carries no `door` field. The caller does not
 * choose the door: a staff caller who names `portal_link` would erase the
 * attribution on a client file. The portal door gets its own endpoint with the
 * portal submit path, and that work is deferred.
 */
export const lockReceiptRequestSchema = z.object({
    by: z.string(),
    sourceUploadId: z.string().min(1),
    note: z.string().max(500).optional(),
});
export type LockReceiptRequest = z.infer<typeof lockReceiptRequestSchema>;

// ---------------------------------------------------------------------------
// FactFindAmendment - the evidenced correction (SEP-243 sections 7 and 42)
// ---------------------------------------------------------------------------

/** Where the correction comes from (SEP-243 section 7). */
export const factFindAmendmentSourceKindSchema = z.enum([
    'email',
    'recorded_call',
    'document',
    'other',
    // SEP-293: the client answered a clarification request in the portal.
    // Only that door writes it; a member of staff never records it.
    'client_portal',
]);

/** The sources that a member of staff may record (all but `client_portal`). */
export const staffAmendmentSourceKindSchema = z.enum([
    'email',
    'recorded_call',
    'document',
    'other',
]);

/**
 * `active` supplies the Current Confirmed Value. `withdrawn` supplies none, and
 * the row stays visible: the file must show that somebody recorded a correction
 * and then withdrew it.
 */
export const factFindAmendmentStatusSchema = z.enum(['active', 'withdrawn']);

/**
 * One evidenced correction to ONE answer of ONE receipt-locked version.
 *
 * `originalValue` is the answer that the receipt lock froze, copied onto the
 * row when somebody records the amendment. The original itself stays on the
 * version, and nothing ever overwrites it (acceptance point 11).
 */
export const factFindAmendmentSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    /** The receipt-locked version that holds the original answer. */
    versionId: z.string(),
    sectionId: z.string(),
    questionId: z.string(),
    originalValue: factFindAnswerSchema,
    correctedValue: factFindAnswerSchema,
    reason: z.string(),
    sourceKind: factFindAmendmentSourceKindSchema,
    /** A message id, a call reference or another pointer into the source. */
    sourceRef: z.string().nullable(),
    /** The evidence documents that the client Documents door stored (ADR-0018). */
    sourceUploadIds: z.array(z.string()),
    recordedByUserId: z.string().nullable(),
    /**
     * The display name of the person who recorded the correction. The seam
     * resolves it from the `users` directory, and a departed member of staff
     * resolves to null. The card shows a name, and never a raw user id.
     */
    recordedByName: z.string().nullable(),
    recordedAt: z.string(),
    status: factFindAmendmentStatusSchema,
    withdrawnAt: z.string().nullable(),
    withdrawnByUserId: z.string().nullable(),
    withdrawalReason: z.string().nullable(),
});
export type FactFindAmendmentDto = z.infer<typeof factFindAmendmentSchema>;

/**
 * One question, with the Original Client Answer beside the Current Confirmed
 * Value.
 *
 * The controller DERIVES this list at the mapping seam with
 * `foldFactFindAmendments` (`packages/contract/src/fact-find-amendment-fold.ts`).
 * No column stores it, and the web layer computes nothing of its own.
 */
export const confirmedFactFindAnswerSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    /** The answer that the receipt lock froze. */
    originalValue: factFindAnswerSchema,
    /** False when the version holds no response for the question at all. */
    hasOriginal: z.boolean(),
    /** The value that the firm acts on today. */
    currentValue: factFindAnswerSchema,
    amended: z.boolean(),
    currentAmendmentId: z.string().nullable(),
    /** Every amendment of the question, oldest first, withdrawn ones included. */
    history: z.array(factFindAmendmentSchema),
});
export type ConfirmedFactFindAnswerDto = z.infer<typeof confirmedFactFindAnswerSchema>;

export const factFindAmendmentsResponseSchema = z.object({
    /** The version the list belongs to, or null when the client holds none. */
    versionId: z.string().nullable(),
    /** Null while the version holds no receipt lock. An amendment needs one. */
    receiptLockedAt: z.string().nullable(),
    /** Every amendment of the version, newest first. */
    amendments: z.array(factFindAmendmentSchema),
    /** The folded answers: the original beside the current confirmed value. */
    confirmed: z.array(confirmedFactFindAnswerSchema),
});
export type FactFindAmendmentsResponse = z.infer<typeof factFindAmendmentsResponseSchema>;

/**
 * The value that a correction can carry.
 *
 * It is `factFindAnswerSchema` with ONE narrowing: a blank string is refused.
 * A blank string blanks the Current Confirmed Value while the card labels the
 * row "Corrected", and the row is append-only, so the slip can only be
 * withdrawn. `null` stays legal, because a client can say that a question has
 * no answer, and that statement is a deliberate choice and not a slip.
 */
export const factFindAmendmentValueSchema = z.union([
    z.string().min(1),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
]);
export type FactFindAmendmentValue = z.infer<typeof factFindAmendmentValueSchema>;

/**
 * Record one evidenced correction.
 *
 * The caller names NEITHER the client NOR the original answer. The server reads
 * both from the version: a caller that named a client could name the wrong one,
 * and a caller that named an original could write a figure the client never
 * gave.
 *
 * `sourceUploadIds` names the documents that the client Documents door already
 * stored. The list can be empty, because the firm asked for the evidence where
 * it is available, and `sourceKind` then carries the provenance.
 */
export const recordAmendmentRequestSchema = z.object({
    by: z.string(),
    versionId: z.string().min(1),
    sectionId: z.string().min(1),
    questionId: z.string().min(1),
    correctedValue: factFindAmendmentValueSchema,
    reason: z.string().min(1).max(2000),
    sourceKind: staffAmendmentSourceKindSchema,
    sourceRef: z.string().max(500).nullable().optional(),
    /**
     * The stored SourceUpload ids. They are UUIDs, which is what the
     * `UploadBacked` capability declares, and the seam refuses an id that names
     * no live document of this client.
     */
    sourceUploadIds: z.array(z.string().uuid()).optional(),
});
export type RecordAmendmentRequest = z.infer<typeof recordAmendmentRequestSchema>;

/** Withdraw one amendment. The row stays, and it holds a withdrawal stamp. */
export const withdrawAmendmentRequestSchema = z.object({
    by: z.string(),
    reason: z.string().min(1).max(2000),
});
export type WithdrawAmendmentRequest = z.infer<typeof withdrawAmendmentRequestSchema>;

export const prefillListResponseSchema = z.object({
    suggestions: z.array(prefillSuggestionSchema),
});
export type PrefillListResponse = z.infer<typeof prefillListResponseSchema>;

export const prefillActionResponseSchema = z.object({
    ok: z.boolean(),
    suggestion: prefillSuggestionSchema,
    /** Present when an accept wrote the value into a draft. */
    factFind: factFindSchema.optional(),
});
export type PrefillActionResponse = z.infer<typeof prefillActionResponseSchema>;

// ---------------------------------------------------------------------------
// The SUI list (SEP-288) - every assessment under one SUR, or of one client
// ---------------------------------------------------------------------------

/** The outcome of a suitability refresh (SEP-288 section 4). */
export const suiOutcomeSchema = z.enum(['A', 'B']);
export type SuiOutcome = z.infer<typeof suiOutcomeSchema>;

/**
 * Where a SUI stands with Compliance. `approved` covers a historic V5 SUI that
 * the CRM approved. `not_submitted` is a SUI that no person sent to Compliance.
 */
export const suiComplianceStatusSchema = z.enum([
    'not_submitted',
    'pending',
    'approved',
    'rejected',
]);
export type SuiComplianceStatus = z.infer<typeof suiComplianceStatusSchema>;

/** One SUI: one fact-find version, as the SUR page and the client page list it. */
export const suiSummarySchema = z.object({
    versionId: z.string(),
    /** The fact find that holds the version. The view action opens it. */
    factFindId: z.string(),
    /** The SUI-#### number. Null on an old version that has none. */
    reference: z.string().nullable(),
    formVersion: factFindFormVersionSchema,
    clientId: z.string(),
    /** The person the SUI is about. Null when it names the client alone. */
    subjectName: z.string().nullable(),
    createdAt: z.string(),
    status: factFindStatusSchema,
    /** The CRM status text of an imported V5 SUI. */
    sourceStatus: z.string().nullable(),
    outcome: suiOutcomeSchema.nullable(),
    expiresAt: z.string().nullable(),
    riskProfile: z.string().nullable(),
    /** The name of the IM who owns the client. */
    assignedIm: z.string().nullable(),
    /** The names of the executive assistants who work that book. */
    assignedAssistants: z.array(z.string()),
    complianceStatus: suiComplianceStatusSchema,
});
export type SuiSummary = z.infer<typeof suiSummarySchema>;

export const suiListResponseSchema = z.object({
    /** The newest SUI that a person created, whatever its status. */
    currentSui: suiSummarySchema.nullable(),
    /**
     * The risk profile of the newest SUI that Compliance approved. A draft
     * never replaces it: a draft shows only its proposed risk profile.
     */
    currentApprovedRiskProfile: z
        .object({
            reference: z.string().nullable(),
            riskProfile: z.string().nullable(),
            approvedAt: z.string().nullable(),
        })
        .nullable(),
    /** Every SUI, newest first. */
    suis: z.array(suiSummarySchema),
});
export type SuiListResponse = z.infer<typeof suiListResponseSchema>;

// ---------------------------------------------------------------------------
// The suggestions of a new fact find (SEP-288 sections 2 and 8)
// ---------------------------------------------------------------------------

/** One prior answer, as the suggestion box beside its field shows it. */
export const factFindSuggestionSchema = z.object({
    sectionId: z.string(),
    questionId: z.string(),
    value: factFindAnswerSchema,
});
export type FactFindSuggestion = z.infer<typeof factFindSuggestionSchema>;

/** One change to the client record since the prior SUI. */
export const factFindRecordChangeSchema = z.object({
    field: z.string(),
    label: z.string(),
    /** The questions of the personal section that the person reviews. */
    questionIds: z.array(z.string()),
    previous: z.string().nullable(),
    current: z.string().nullable(),
    changedAt: z.string(),
});
export type FactFindRecordChange = z.infer<typeof factFindRecordChangeSchema>;

export const factFindSuggestionsResponseSchema = z.object({
    /** The prior SUI that the suggestions come from. Null when there is none. */
    source: z
        .object({
            versionId: z.string(),
            reference: z.string().nullable(),
            formVersion: factFindFormVersionSchema,
            createdAt: z.string(),
        })
        .nullable(),
    /** One prior answer for each question that the prior SUI answered. */
    answers: z.array(factFindSuggestionSchema),
    /** The prior risk answers: question id -> option label. */
    atr: z.record(z.string(), z.string()),
    /** The client-record changes since the prior SUI. */
    recordChanges: z.array(factFindRecordChangeSchema),
});
export type FactFindSuggestionsResponse = z.infer<typeof factFindSuggestionsResponseSchema>;

/**
 * Create a new V6 fact find (SEP-288 section 2, SEP-353). Name one fact find,
 * or a SUR. A SUR gives one new SUI on THE fact find of the SUR, whatever the
 * number of its contacts and accounts, and makes the fact find when the SUR
 * holds none yet.
 */
export const createNewFactFindRequestSchema = z.object({
    factFindId: z.string().optional(),
    suitabilityRecordId: z.string().optional(),
    by: z.string().min(1),
});
export type CreateNewFactFindRequest = z.infer<typeof createNewFactFindRequestSchema>;

export const createNewFactFindResponseSchema = z.object({
    ok: z.boolean(),
    /** The fact finds that hold a new draft, in the order of the people. */
    factFindIds: z.array(z.string()),
});
export type CreateNewFactFindResponse = z.infer<typeof createNewFactFindResponseSchema>;

// ---------------------------------------------------------------------------
// The audit history of one SUI (SEP-288 section 9)
// ---------------------------------------------------------------------------

/** One event of the permanent history of one SUI. Nobody edits or deletes one. */
export const suiHistoryEntrySchema = z.object({
    id: z.string(),
    at: z.string(),
    /** The name of the user, or the name of the system process. */
    by: z.string(),
    action: z.string(),
    detail: z.string().nullable(),
    field: z.string().nullable(),
    previousValue: z.string().nullable(),
    newValue: z.string().nullable(),
    reason: z.string().nullable(),
    /** Where the event comes from: the fact find, the refresh or an amendment. */
    source: z.enum(['fact_find', 'refresh', 'amendment']),
});
export type SuiHistoryEntry = z.infer<typeof suiHistoryEntrySchema>;

export const suiHistoryResponseSchema = z.object({ entries: z.array(suiHistoryEntrySchema) });
export type SuiHistoryResponse = z.infer<typeof suiHistoryResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const factFindContract = c.router(
    {
        listRegister: {
            method: 'GET',
            path: '/fact-find/clients',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: factFindRegisterResponseSchema },
            summary: 'Fact-find register — one row per fact find',
        },
        listClientOptions: {
            method: 'GET',
            path: '/fact-find/client-options',
            query: z.object({ q: z.string().optional() }).optional(),
            responses: { 200: factFindClientsResponseSchema },
            summary: 'List clients for the manual-create typeahead',
        },
        listRecordOptions: {
            method: 'GET',
            path: '/fact-find/record-options',
            query: z
                .object({
                    /** Only the SURs that cover this account group. */
                    clientId: z.string().optional(),
                    /** Only this SUR: the preview of a refresh. */
                    recordId: z.string().optional(),
                    search: z.string().optional(),
                })
                .optional(),
            responses: { 200: factFindRecordOptionsResponseSchema },
            summary:
                'The Suitability Records a fact find can start on, with their accounts, contacts and newest fact find (SEP-353)',
        },
        listAccountGroupOptions: {
            method: 'GET',
            path: '/fact-find/account-group-options',
            responses: { 200: factFindAccountGroupOptionsResponseSchema },
            summary:
                'The account groups a new Suitability Record can cover, with their accounts and SURs (SEP-353)',
        },
        listContactFactFinds: {
            method: 'GET',
            path: '/fact-find/contact-fact-finds',
            query: z.object({ contactId: z.string().min(1) }),
            responses: { 200: contactFactFindsResponseSchema, 404: errorBody },
            summary: 'The fact finds one contact is the subject of, for the contact page (SEP-261)',
        },
        getSuggestions: {
            method: 'GET',
            path: '/fact-find/:factFindId/suggestions',
            query: z.object({ versionId: z.string() }),
            responses: { 200: factFindSuggestionsResponseSchema, 404: errorBody },
            summary:
                'The prior answers and the client-record changes for the suggestion boxes of a new fact find (SEP-288)',
        },
        createNewFactFind: {
            method: 'POST',
            path: '/fact-find/new-fact-find',
            body: createNewFactFindRequestSchema,
            responses: {
                200: createNewFactFindResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Create a new V6 fact find with a new SUI on one fact find, or on the fact find of a SUR (SEP-288, SEP-353)',
        },
        getSuiHistory: {
            method: 'GET',
            path: '/fact-find/:factFindId/versions/:versionId/history',
            responses: { 200: suiHistoryResponseSchema, 404: errorBody },
            summary: 'The permanent audit history of one SUI, oldest first (SEP-288)',
        },
        listSuis: {
            method: 'GET',
            path: '/fact-find/suis',
            query: z.object({
                clientId: z.string().optional(),
                suitabilityRecordId: z.string().optional(),
            }),
            responses: { 200: suiListResponseSchema, 400: errorBody, 404: errorBody },
            summary:
                'Every SUI of one client or under one SUR, with the Current SUI and the Current Approved Risk Profile (SEP-288)',
        },
        riskSummary: {
            method: 'GET',
            path: '/fact-find/risk-summary',
            query: z.object({ imId: z.string().optional() }).optional(),
            responses: { 200: factFindRiskSummaryResponseSchema },
            summary: 'Per-client risk score (band / mandate / capacity flags) from the fact find',
        },
        createFactFind: {
            method: 'POST',
            path: '/fact-find/create',
            body: createFactFindRequestSchema,
            responses: {
                200: createFactFindResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary:
                'Manually create a fact find for a client that no Suitability Record covers yet, existing or brand-new',
        },
        getFactFind: {
            method: 'GET',
            path: '/fact-find/:factFindId',
            responses: { 200: factFindResponseBody, 404: errorBody },
            summary: 'Get a client fact find with full version history',
        },
        createDraft: {
            method: 'POST',
            path: '/fact-find/:factFindId/draft',
            body: createDraftRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Create a new draft version of the fact find',
        },
        saveWorking: {
            method: 'PATCH',
            path: '/fact-find/:factFindId/working',
            body: saveWorkingRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Atomically save all sections + ATR + CFL onto the working version',
        },
        transition: {
            method: 'POST',
            path: '/fact-find/:factFindId/transition',
            body: factFindTransitionRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Move the working version through its lifecycle',
        },
        deleteFactFind: {
            method: 'POST',
            path: '/fact-find/:factFindId/delete',
            body: deleteFactFindRequestSchema,
            responses: {
                200: deleteFactFindResponseSchema,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Soft-delete a fact find (retained in Postgres, hidden from the register)',
        },
        adminDeleteFactFind: {
            method: 'POST',
            path: '/fact-find/:factFindId/admin-delete',
            body: adminDeleteFactFindRequestSchema,
            responses: {
                200: adminDeleteFactFindResponseSchema,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Delete a fact find and the rows that exist only for it (admin only)',
        },
        lockReceipt: {
            method: 'POST',
            path: '/fact-find/:factFindId/lock-receipt',
            body: lockReceiptRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Confirm the receipt of the client fact find and freeze its answers',
        },
        // --- The evidenced amendment (SEP-243 sections 7 and 42) ---
        //
        // A locked original answer is never edited. A correction lands as a NEW
        // row beside the version, and the Current Confirmed Value is DERIVED
        // from the two. The read endpoint returns the amendment list and the
        // folded answers together, so a reader sees both figures at once.
        listAmendments: {
            method: 'GET',
            path: '/fact-find/:factFindId/amendments',
            query: z.object({ versionId: z.string().optional() }).optional(),
            responses: { 200: factFindAmendmentsResponseSchema, 404: errorBody },
            summary: 'List the amendments of a version, with the folded confirmed answers',
        },
        recordAmendment: {
            method: 'POST',
            path: '/fact-find/:factFindId/amendment',
            body: recordAmendmentRequestSchema,
            // 409 when the target version holds no receipt lock: before the
            // lock a correction is an ordinary edit, and the section save
            // already takes it.
            responses: {
                200: factFindAmendmentsResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Record one evidenced correction beside the original client answer',
        },
        withdrawAmendment: {
            method: 'POST',
            path: '/fact-find/:factFindId/amendment/:amendmentId/withdraw',
            body: withdrawAmendmentRequestSchema,
            responses: {
                200: factFindAmendmentsResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Withdraw one amendment. The row stays with a withdrawal stamp.',
        },
        addAnnotation: {
            method: 'POST',
            path: '/fact-find/:factFindId/annotation',
            body: addAnnotationRequestSchema,
            responses: {
                200: factFindResponseBody,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Add an IM annotation against a section',
        },
        refresh: {
            method: 'POST',
            path: '/fact-find/:factFindId/refresh',
            body: refreshRequestSchema,
            responses: {
                200: refreshResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Author a section-targeted refresh and send the portal link',
        },
        listPrefill: {
            method: 'GET',
            path: '/fact-find/:factFindId/prefill',
            responses: { 200: prefillListResponseSchema, 404: errorBody },
            summary: 'List the ATLAS pre-fill suggestions of the client of this fact find',
        },
        acceptPrefill: {
            method: 'POST',
            path: '/fact-find/prefill/:suggestionId/accept',
            // `factFindId` names WHICH fact find of the client takes the value
            // (SEP-261). A suggestion belongs to the client, and a client can
            // hold several fact finds, so the workbench sends the handle of the
            // one on screen. Without it the server writes to the client-level
            // fact find of that client.
            body: z.object({ by: z.string(), factFindId: z.string().min(1).optional() }),
            // 409 when the working version no longer takes an answer edit: it
            // is submitted (`fact_find_submitted_locked`) or it holds a receipt
            // lock (`fact_find_receipt_locked`). The refusal comes BEFORE the
            // suggestion moves, so a later accept still works.
            responses: {
                200: prefillActionResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Accept a pre-fill suggestion and write it into the draft',
        },
        rejectPrefill: {
            method: 'POST',
            path: '/fact-find/prefill/:suggestionId/reject',
            body: z.object({ by: z.string() }),
            responses: { 200: prefillActionResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Reject a pre-fill suggestion',
        },
        getDocument: {
            method: 'GET',
            path: '/fact-find/:factFindId/document',
            responses: { 200: factFindDocumentResponseSchema, 404: errorBody },
            summary: 'Get the generated fact-find document meta for a client',
        },
        generateDocument: {
            method: 'POST',
            path: '/fact-find/:factFindId/document',
            body: z.object({ by: z.string() }),
            responses: { 200: factFindDocumentResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Generate / regenerate the filled fact-find PDF and return its meta',
        },
        getSigningState: {
            method: 'GET',
            path: '/fact-find/:factFindId/signing',
            responses: { 200: factFindSigningStateResponseSchema, 404: errorBody },
            summary: 'Signing state for the client fact-find view (gates the DocuSign affordance)',
        },
        sendForSignature: {
            method: 'POST',
            path: '/fact-find/:factFindId/send-for-signature',
            body: sendForSignatureRequestSchema,
            responses: {
                200: factFindSigningStateResponseSchema,
                400: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Send the filled fact-find PDF for DocuSign signature',
        },
        markSigned: {
            method: 'POST',
            path: '/fact-find/:factFindId/mark-signed',
            body: markSignedRequestSchema,
            responses: { 200: factFindSigningStateResponseSchema, 400: errorBody, 404: errorBody },
            summary: 'Mark the fact-find document signed (manual, without an uploaded copy)',
        },
        draftClientEmail: {
            method: 'POST',
            path: '/fact-find/:factFindId/draft-client-email',
            body: draftClientEmailRequestSchema,
            responses: {
                200: draftClientEmailResponseSchema,
                404: errorBody,
                // 409 = chosen provider not configured/connected; 422 = client has no email on file.
                409: errorBody,
                422: errorBody,
            },
            summary:
                "Draft a client cover-note email into the IM's connected mailbox (Outlook / Gmail)",
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
